import { useEffect, useState } from "react";
import styled from "styled-components";
import { FiTrash2, FiUserPlus } from "react-icons/fi";

import FormComp from "../../Componentes/FormComp";
import Overlay from "../../Componentes/Overlay";
import Formulario from "../../Componentes/Utils/Formulario";
import Modal from "../../Componentes/Utils/Modal";
import { useModal } from "../../contexts/ModalContext";
import useApi from "../../hooks/useApi";
import { formatBRLFromCents, parseCentsFromInput } from "../../Funcoes/Money";

/**
 * Motivos disponíveis para lançamento manual na folha.
 *
 * Observação: VA OBRA / CARTÃO ALELO, VALE TRANSPORTE e DIARIA DE VIAGEM
 * foram removidos a pedido — esses valores entram por outra rotina
 * (benefícios TOTVS / tabela colaboradores).
 */
const MOTIVOS = [
    "ADC PF",
    "COMPRA FOLGA DE CAMPO",
    "DESCONTO DIVERSOS",
    "FOLGA DE DOMINGO",
    "PLR",
    "REEMBOLSO DE PASSAGENS",
    "REEMBOLSO DE V.A",
    "REEMBOLSO DE V.T",
];

const AddColabRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 8px;

    > div {
        flex: 1;
    }
`;

const AddButton = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 40px;
    padding: 0 14px;
    border-radius: 8px;
    background-color: #0f766e;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: background-color .15s ease, opacity .15s ease;
    box-sizing: border-box;

    &:hover:not(:disabled) {
        background-color: #115e59;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ColabList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 180px;
    overflow-y: auto;
`;

const ColabItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background-color: #f5f7fa;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 13px;
`;

const ColabInfo = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
        font-size: 12px;
        color: #0f766e;
    }

    span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const RemoveButton = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    color: #b91c1c;
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color .15s ease;

    &:hover {
        background-color: rgba(185, 28, 28, 0.12);
    }
`;

const ListLabel = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
`;

const ModalNovoLancamento = ({ centrosCustoOpts = [], onLancamentoCreated }) => {

    const { closeModal } = useModal();
    const api = useApi();

    const [saving, setSaving] = useState(false);
    const [loadingNome, setLoadingNome] = useState(false);
    const [colaborador, setColaborador] = useState("");
    const [nomeEncontrado, setNomeEncontrado] = useState(false);

    /* Lista de colaboradores adicionados ao lançamento: { matricula, nome } */
    const [colaboradores, setColaboradores] = useState([]);

    const [formValues, setFormValues] = useState({
        obra: "",
        motivo: "",
        matricula: "",
        valor: 0,
        data: "",
        observacoes: "",
    });

    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormValues((prev) => {

            if (name === "valor") {
                return { ...prev, valor: parseCentsFromInput(value) };
            }

            if (name === "matricula") {
                const onlyDigits = String(value || "").replace(/\D/g, "").slice(0, 6);
                return { ...prev, matricula: onlyDigits };
            }

            if (name === "observacoes") {
                return { ...prev, observacoes: value };
            }

            return { ...prev, [name]: value };

        });

    }

    /* Busca automática do nome do colaborador quando a matrícula tem 6 dígitos.
       Mesma regra da tela antiga (OverlayPagamento). */
    useEffect(() => {

        setNomeEncontrado(false);

        if (formValues.matricula.length !== 6) {
            setColaborador("");
            return;
        }

        let cancelled = false;

        const fetchNome = async () => {

            setLoadingNome(true);
            setColaborador("CARREGANDO...");

            try {

                const { data } = await api.post("/Painel/API/GetColabName/", {
                    matricula: formValues.matricula,
                });

                if (cancelled) return;

                if (data?.STATUS === "SUCCESS") {
                    setColaborador(data.NOME || "");
                    setNomeEncontrado(Boolean(data.NOME));
                } else {
                    setColaborador("COLABORADOR NÃO ENCONTRADO");
                }

            } catch (e) {

                if (cancelled) return;
                console.error(e);
                setColaborador("ERRO AO BUSCAR COLABORADOR");

            } finally {

                if (!cancelled) setLoadingNome(false);

            }

        }

        fetchNome();

        return () => {
            cancelled = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.matricula]);

    const podeAdicionar = nomeEncontrado && !loadingNome;

    const handleAddColaborador = () => {

        if (!podeAdicionar) return;

        const matricula = formValues.matricula;

        if (colaboradores.some((c) => c.matricula === matricula)) {
            alert("Este colaborador já foi adicionado.");
            return;
        }

        setColaboradores((prev) => [...prev, { matricula, nome: colaborador }]);
        setFormValues((prev) => ({ ...prev, matricula: "" }));
        setColaborador("");
        setNomeEncontrado(false);

    }

    const handleRemoveColaborador = (matricula) => {
        setColaboradores((prev) => prev.filter((c) => c.matricula !== matricula));
    }

    const handleSubmit = async (e) => {

        e.preventDefault();

        if (!formValues.obra) {
            alert("Selecione o centro de custo.");
            return;
        }
        if (!formValues.motivo) {
            alert("Selecione o motivo.");
            return;
        }

        /* Conveniência: se o usuário digitou uma matrícula válida e não clicou
           em adicionar, ela entra no lançamento junto com a lista. */
        const listaFinal = [...colaboradores];
        if (podeAdicionar && !listaFinal.some((c) => c.matricula === formValues.matricula)) {
            listaFinal.push({ matricula: formValues.matricula, nome: colaborador });
        }

        if (listaFinal.length === 0) {
            alert("Adicione pelo menos um colaborador.");
            return;
        }
        if (!formValues.valor || formValues.valor === 0) {
            alert("Informe o valor.");
            return;
        }
        if (!formValues.data) {
            alert("Informe a data.");
            return;
        }

        try {

            setSaving(true);

            const payload = {
                obra: formValues.obra,
                motivo: formValues.motivo,
                matriculas: listaFinal.map((c) => c.matricula),
                valor: formValues.valor / 100,
                data: formValues.data,
                observacoes: formValues.observacoes || null,
            };

            await api.post("/Painel/API/FolhaPag/Lancamentos/", payload);

            closeModal();

            if (onLancamentoCreated) {
                onLancamentoCreated();
            }

        } catch (err) {

            console.error(err);
            alert("Erro ao salvar lançamento.");

        } finally {

            setSaving(false);

        }

    }

    return (
        <Overlay state={ true } >
            <Modal header close={ closeModal } >
                <Formulario onSubmit={ handleSubmit } >
                    <FormComp
                        title="CENTRO DE CUSTO"
                        type="select"
                        options={ centrosCustoOpts }
                        valor={ formValues.obra }
                        name="obra"
                        changeFunc={ handleChange }
                        required
                    />
                    <FormComp
                        title="MOTIVO"
                        type="select"
                        options={ MOTIVOS }
                        valor={ formValues.motivo }
                        name="motivo"
                        changeFunc={ handleChange }
                        required
                    />
                    <AddColabRow>
                        <FormComp
                            title="MATRÍCULA"
                            type="text"
                            inputMode="numeric"
                            maxLength={ 6 }
                            placeholder="EX: 001234"
                            valor={ formValues.matricula }
                            name="matricula"
                            changeFunc={ handleChange }
                        />
                        <AddButton
                            type="button"
                            onClick={ handleAddColaborador }
                            disabled={ !podeAdicionar }
                            title="Adicionar colaborador à lista"
                        >
                            <FiUserPlus size={ 15 } />
                            ADICIONAR
                        </AddButton>
                    </AddColabRow>
                    <FormComp
                        title="COLABORADOR"
                        type="text"
                        readOnly
                        valor={ loadingNome ? "CARREGANDO..." : colaborador }
                    />
                    { colaboradores.length > 0 && (
                        <>
                            <ListLabel>
                                COLABORADORES ADICIONADOS ({ colaboradores.length })
                            </ListLabel>
                            <ColabList>
                                { colaboradores.map((colab) => (
                                    <ColabItem key={ colab.matricula }>
                                        <ColabInfo>
                                            <strong>{ colab.matricula }</strong>
                                            <span>{ colab.nome }</span>
                                        </ColabInfo>
                                        <RemoveButton
                                            type="button"
                                            onClick={ () => handleRemoveColaborador(colab.matricula) }
                                            title="Remover colaborador"
                                        >
                                            <FiTrash2 size={ 15 } />
                                        </RemoveButton>
                                    </ColabItem>
                                )) }
                            </ColabList>
                        </>
                    ) }
                    <FormComp
                        title="VALOR"
                        type="text"
                        inputMode="numeric"
                        placeholder="R$ 0,00"
                        valor={ formatBRLFromCents(formValues.valor) }
                        name="valor"
                        changeFunc={ handleChange }
                        required
                    />
                    <FormComp
                        title="DATA"
                        type="date"
                        valor={ formValues.data }
                        name="data"
                        changeFunc={ handleChange }
                        required
                    />
                    <FormComp
                        title="OBSERVAÇÕES"
                        type="textarea"
                        valor={ formValues.observacoes }
                        name="observacoes"
                        changeFunc={ handleChange }
                    />
                    <FormComp
                        type="submit"
                        valor={ saving ? "SALVANDO..." : "SALVAR" }
                        disabled={ saving }
                    />
                </Formulario>
            </Modal>
        </Overlay>
    )

}

export default ModalNovoLancamento;
